package com.evoapps.evoshift.ui.calendar

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.OpenInNew
import androidx.compose.material.icons.filled.ContentCopy
import androidx.compose.material.icons.filled.Download
import androidx.compose.material.icons.filled.Event
import androidx.compose.material.icons.filled.QrCode
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalClipboard
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.calendar.CalendarApp
import com.evoapps.evoshift.domain.calendar.CalendarSubscriptionLinks
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.util.plainTextClipEntry
import com.evoapps.evoshift.viewmodels.CalendarConfirm
import com.evoapps.evoshift.viewmodels.CalendarHandOff
import com.evoapps.evoshift.viewmodels.CalendarState
import com.evoapps.evoshift.viewmodels.CalendarViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.DialogSection
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.ErrorOnNavy
import com.evoapps.evoshift.widgets.EvoConfirmDialog
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoListRow
import com.evoapps.evoshift.widgets.EvoSecondaryButton
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.add_to_calendar_download
import evoshift.composeapp.generated.resources.add_to_calendar_hint
import evoshift.composeapp.generated.resources.calendar_app_apple
import evoshift.composeapp.generated.resources.calendar_app_apple_hint
import evoshift.composeapp.generated.resources.calendar_app_google
import evoshift.composeapp.generated.resources.calendar_app_google_hint
import evoshift.composeapp.generated.resources.calendar_app_outlook
import evoshift.composeapp.generated.resources.calendar_app_outlook_hint
import evoshift.composeapp.generated.resources.calendar_app_outlook_work
import evoshift.composeapp.generated.resources.calendar_app_outlook_work_hint
import evoshift.composeapp.generated.resources.calendar_link_copy
import evoshift.composeapp.generated.resources.calendar_link_new
import evoshift.composeapp.generated.resources.calendar_link_off
import evoshift.composeapp.generated.resources.calendar_sub_copy_hint
import evoshift.composeapp.generated.resources.calendar_sub_file_title
import evoshift.composeapp.generated.resources.calendar_sub_intro
import evoshift.composeapp.generated.resources.calendar_sub_link_elsewhere
import evoshift.composeapp.generated.resources.calendar_sub_link_none
import evoshift.composeapp.generated.resources.calendar_sub_link_on
import evoshift.composeapp.generated.resources.calendar_sub_link_on_undated
import evoshift.composeapp.generated.resources.calendar_sub_manage
import evoshift.composeapp.generated.resources.calendar_sub_new_text
import evoshift.composeapp.generated.resources.calendar_sub_new_title
import evoshift.composeapp.generated.resources.calendar_sub_off_text
import evoshift.composeapp.generated.resources.calendar_sub_off_title
import evoshift.composeapp.generated.resources.calendar_sub_pick
import evoshift.composeapp.generated.resources.calendar_sub_privacy
import evoshift.composeapp.generated.resources.calendar_sub_qr
import evoshift.composeapp.generated.resources.calendar_sub_qr_description
import evoshift.composeapp.generated.resources.calendar_sub_qr_show
import evoshift.composeapp.generated.resources.calendar_sub_title
import evoshift.composeapp.generated.resources.close
import io.github.alexzhirkevich.qrose.options.QrErrorCorrectionLevel
import io.github.alexzhirkevich.qrose.options.QrOptions
import io.github.alexzhirkevich.qrose.rememberQrCodePainter
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Instant

/** A month whose published shifts can also be saved as an .ics file (opened from My shifts). */
data class CalendarFileMonth(val rosterId: String, val label: String)

/** From this width the QR code stands next to the buttons. */
private val SIDE_BY_SIDE = 520.dp

object CalendarSubscriptionTags {
    const val QR = "calendar_qr"
    fun app(app: CalendarApp) = "calendar_app_${app.name.lowercase()}"
    const val COPY = "calendar_copy"
}

/**
 * "Your shifts in your calendar" (SH-202): what the subscription does in one sentence, one button
 * per calendar app that opens its add-a-subscription page with the private link (made on the first
 * tap), "Copy link" for other apps, a QR code on desktop and web, what the link shows, and managing
 * the link (a new one, or off; both asked first). Opened from My shifts ("Add to calendar", with
 * [month]'s .ics file too) and from the profile.
 */
@Composable
fun CalendarSubscriptionDialog(onDismiss: () -> Unit, month: CalendarFileMonth? = null) {
    val viewModel: CalendarViewModel = diViewModel()
    val state by viewModel.state.collectAsState()
    LaunchedEffect(Unit) { viewModel.open() }
    val clipboard = LocalClipboard.current
    val uriHandler = LocalUriHandler.current

    LaunchedEffect(state.handOff) {
        when (val handOff = state.handOff) {
            is CalendarHandOff.Open -> {
                // No app for webcal (an Android phone without one): the link is copied instead.
                if (runCatching { uriHandler.openUri(handOff.uri) }.isFailure) {
                    clipboard.setClipEntry(plainTextClipEntry(CalendarSubscriptionLinks.https(handOff.uri)))
                }
            }
            is CalendarHandOff.Copy -> clipboard.setClipEntry(plainTextClipEntry(handOff.text))
            null -> return@LaunchedEffect
        }
        viewModel.handedOff()
    }

    EvoDialog(
        title = stringResource(Res.string.calendar_sub_title),
        icon = Icons.Default.Event,
        onDismiss = onDismiss,
        width = if (state.showQr) DialogWidth.Wide else DialogWidth.Regular,
        dismissible = !state.busy,
        confirm = null,
        dismiss = DialogAction(stringResource(Res.string.close), onDismiss, enabled = !state.busy),
        bodyArrangement = Arrangement.spacedBy(12.dp)
    ) {
        Text(
            stringResource(Res.string.calendar_sub_intro),
            color = Color.White.copy(alpha = 0.85f),
            style = MaterialTheme.typography.bodyMedium
        )
        if (state.busy) LinearProgressIndicator(Modifier.fillMaxWidth(), color = LightGold)

        DialogSection(stringResource(Res.string.calendar_sub_pick)) {
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                when {
                    !state.showQr -> Column(verticalArrangement = Arrangement.spacedBy(8.dp)) { CalendarButtons(state, viewModel) }
                    maxWidth >= SIDE_BY_SIDE -> Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) { CalendarButtons(state, viewModel) }
                        QrPanel(state, viewModel::showQr, Modifier.width(200.dp))
                    }
                    else -> Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        CalendarButtons(state, viewModel)
                        QrPanel(state, viewModel::showQr, Modifier.fillMaxWidth().padding(top = 8.dp))
                    }
                }
            }
        }

        Text(
            stringResource(Res.string.calendar_sub_privacy),
            color = Color.White.copy(alpha = 0.7f),
            style = MaterialTheme.typography.bodySmall
        )

        DialogSection(stringResource(Res.string.calendar_sub_manage), Modifier.padding(top = 4.dp)) {
            Text(linkStatus(state), color = Color.White, style = MaterialTheme.typography.bodySmall)
            if (state.linkElsewhere) {
                Text(stringResource(Res.string.calendar_sub_link_elsewhere), color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
            }
            if (state.linkActive) {
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    EvoSecondaryButton(DialogAction(stringResource(Res.string.calendar_link_new), viewModel::askNewLink, enabled = !state.busy))
                    EvoSecondaryButton(
                        DialogAction(stringResource(Res.string.calendar_link_off), viewModel::askTurnOff, enabled = !state.busy, destructive = true)
                    )
                }
            }
        }

        if (month != null) {
            DialogSection(stringResource(Res.string.calendar_sub_file_title), Modifier.padding(top = 4.dp)) {
                Text(
                    stringResource(Res.string.add_to_calendar_hint, month.label),
                    color = Color.White.copy(alpha = 0.7f),
                    style = MaterialTheme.typography.bodySmall
                )
                EvoSecondaryButton(
                    DialogAction(
                        stringResource(Res.string.add_to_calendar_download),
                        { viewModel.download(month.rosterId) },
                        enabled = !state.busy,
                        icon = Icons.Default.Download
                    )
                )
            }
        }
    }

    when (state.confirm) {
        CalendarConfirm.NewLink, is CalendarConfirm.Replace -> EvoConfirmDialog(
            title = stringResource(Res.string.calendar_sub_new_title),
            text = stringResource(Res.string.calendar_sub_new_text),
            confirmLabel = stringResource(Res.string.calendar_link_new),
            onConfirm = viewModel::confirm,
            onDismiss = viewModel::dismissConfirm
        )
        CalendarConfirm.TurnOff -> EvoConfirmDialog(
            title = stringResource(Res.string.calendar_sub_off_title),
            text = stringResource(Res.string.calendar_sub_off_text),
            confirmLabel = stringResource(Res.string.calendar_link_off),
            onConfirm = viewModel::confirm,
            onDismiss = viewModel::dismissConfirm,
            destructive = true
        )
        null -> Unit
    }
}

/** One big row per calendar app in the platform's order, then "Copy link", then what happened. */
@Composable
private fun ColumnScope.CalendarButtons(state: CalendarState, viewModel: CalendarViewModel) {
    state.apps.forEach { app ->
        val (name, hint) = app.texts()
        EvoListRow(
            headline = stringResource(name),
            supporting = stringResource(hint),
            onClick = { viewModel.addTo(app) },
            leadingIcon = Icons.Default.Event,
            modifier = Modifier.testTag(CalendarSubscriptionTags.app(app)),
            trailing = { Icon(Icons.AutoMirrored.Filled.OpenInNew, contentDescription = null, tint = LightGold, modifier = Modifier.size(20.dp)) }
        )
    }
    EvoListRow(
        headline = stringResource(Res.string.calendar_link_copy),
        supporting = stringResource(Res.string.calendar_sub_copy_hint),
        onClick = viewModel::copyLink,
        leadingIcon = Icons.Default.ContentCopy,
        modifier = Modifier.testTag(CalendarSubscriptionTags.COPY),
        trailing = {}
    )
    state.message?.let { Text(it.asString(), color = LightGold, style = MaterialTheme.typography.bodyMedium) }
    state.error?.let { Text(it.asString(), color = ErrorOnNavy, style = MaterialTheme.typography.bodySmall) }
}

/** Desktop and web: the link as a QR code for the phone (a button first while this device has no link). */
@Composable
private fun QrPanel(state: CalendarState, onShow: () -> Unit, modifier: Modifier = Modifier) {
    Column(modifier, horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
        val link = state.qrLink
        if (link != null) {
            val painter = rememberQrCodePainter(link, remember { QrOptions(errorCorrectionLevel = QrErrorCorrectionLevel.Medium) })
            Image(
                painter = painter,
                contentDescription = stringResource(Res.string.calendar_sub_qr_description),
                modifier = Modifier.size(176.dp).clip(RoundedCornerShape(12.dp)).background(Color.White).padding(10.dp)
                    .testTag(CalendarSubscriptionTags.QR)
            )
        } else {
            EvoSecondaryButton(
                DialogAction(stringResource(Res.string.calendar_sub_qr_show), onShow, enabled = state.linkLoaded && !state.busy, icon = Icons.Default.QrCode)
            )
        }
        Text(stringResource(Res.string.calendar_sub_qr), color = Color.White.copy(alpha = 0.7f), style = MaterialTheme.typography.bodySmall)
    }
}

@Composable
private fun linkStatus(state: CalendarState): String {
    if (!state.linkActive) return stringResource(Res.string.calendar_sub_link_none)
    val date = state.createdAt?.let { runCatching { Instant.parse(it).toLocalDateTime(TimeZone.currentSystemDefault()).date }.getOrNull() }
    return if (date != null) stringResource(Res.string.calendar_sub_link_on, dateText(date))
    else stringResource(Res.string.calendar_sub_link_on_undated)
}

@Composable
private fun dateText(date: LocalDate): String = "${dayAndMonth(date)} ${date.year}"

private fun CalendarApp.texts(): Pair<StringResource, StringResource> = when (this) {
    CalendarApp.APPLE -> Res.string.calendar_app_apple to Res.string.calendar_app_apple_hint
    CalendarApp.GOOGLE -> Res.string.calendar_app_google to Res.string.calendar_app_google_hint
    CalendarApp.OUTLOOK -> Res.string.calendar_app_outlook to Res.string.calendar_app_outlook_hint
    CalendarApp.OUTLOOK_WORK -> Res.string.calendar_app_outlook_work to Res.string.calendar_app_outlook_work_hint
}
